import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FHIR from 'fhirclient';
import { useSessionStore } from '@/stores/sessionStore';

/**
 * SmartLaunch Component
 * Handles the SMART on FHIR EHR Launch protocol using the fhirclient library.
 *
 * EHR Launch Flow:
 *   1. EHR redirects user to /launch?iss=<fhir-server>&launch=<token>
 *   2. fhirclient discovers the EHR's auth endpoints via .well-known/smart-configuration
 *   3. fhirclient builds an OAuth2 authorization URL with PKCE
 *   4. User is redirected to the EHR's authorization server
 *   5. After consent, EHR redirects back to /callback with an authorization code
 *
 * Spec: https://hl7.org/fhir/smart-app-launch/app-launch.html#launch-app-ehr-launch
 */
export function SmartLaunch() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  // React StrictMode dev fires effects twice. fhirclient.oauth2.authorize
  // stores OAuth state + PKCE verifier in sessionStorage then redirects.
  // Without a guard, the second mount overwrites the first's state with a
  // new one — KC redirects back with the first's state, fhirclient can't
  // match it, throws "No 'state' parameter found". Same fix shape as
  // AuthProvider.jsx + SmartCallback.jsx.
  const authorizeStartedRef = useRef(false);

  useEffect(() => {
    if (authorizeStartedRef.current) {
      console.log('[SMART Launch] authorize already in flight (StrictMode double-mount) — skipping');
      return;
    }
    authorizeStartedRef.current = true;

    const iss = searchParams.get('iss');
    const launch = searchParams.get('launch');

    // TV-LM-2 — read the optional `mode` param ('lite' enables the
    // simplified UI; default 'full'). Persist BEFORE the OAuth redirect
    // so the value survives the round-trip through KC + /callback;
    // sessionStorage is the same scope fhirclient uses.
    const mode = searchParams.get('mode');
    useSessionStore.getState().setUiMode(mode === 'lite' ? 'lite' : 'full');

    // TV-RV-04/12 — persist the launcher's role hint (?role=front-desk|provider)
    // BEFORE the OAuth redirect. The callback URL does NOT carry the original
    // launch query string, so without this the hint is lost and dual-role staff
    // don't get the launcher-wins default. sessionStorage survives the KC
    // round-trip (same scope fhirclient uses). Cleared in SmartCallback.
    const roleHint = searchParams.get('role');
    if (roleHint) {
      sessionStorage.setItem('tvRoleHint', roleHint);
    } else {
      sessionStorage.removeItem('tvRoleHint');
    }

    if (!iss || !launch) {
      setError(
        'Missing required SMART launch parameters. ' +
        'This app must be launched from an EHR with ?iss=<fhir-server>&launch=<token>'
      );
      return;
    }

    // Use the fhirclient library to handle the full SMART authorization handshake.
    // This handles:
    //   - .well-known/smart-configuration discovery
    //   - PKCE challenge generation (required by many EHRs like Epic, Cerner)
    //   - OAuth2 state parameter for CSRF protection
    //   - Proper scope negotiation
    FHIR.oauth2.authorize({
      iss,
      launch,
      clientId: import.meta.env.VITE_SMART_CLIENT_ID || 'collabkare-televisit',
      scope: [
        'launch',                // EHR launch context
        'patient/*.read',        // Read all patient-compartment resources
        'user/*.read',           // Read resources the user has access to
        'openid',                // OpenID Connect identity
        'profile',               // User profile claims
        'fhirUser',              // FHIR user reference (Practitioner/Patient)
        'online_access',         // Refresh tokens for online access
      ].join(' '),
      redirectUri: `${window.location.origin}/callback`,
      // Optional: if your app is registered with a different client ID per EHR
      // completeInTarget: true,
    }).catch((err) => {
      console.error('[SMART Launch] Authorization failed:', err);
      setError(`SMART launch failed: ${err.message}`);
    });
  }, [searchParams]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-primary">
        <div className="bg-surface-card border border-border rounded-xl p-8 max-w-md text-center">
          <div className="text-red-400 text-4xl mb-4">!</div>
          <h2 className="text-xl font-semibold text-white mb-2">Launch Error</h2>
          <p className="text-gray-400 text-sm">{error}</p>
          <p className="text-gray-500 text-xs mt-4">
            If you are a patient or provider, please launch this app from your EHR system.
            If you have a direct link, use the{' '}
            <a href="/" className="text-blue-400 underline">standalone login</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-surface-primary">
      <div className="text-center">
        <div className="inline-block">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
        <h2 className="mt-4 text-xl font-semibold text-white">Launching from EHR</h2>
        <p className="mt-2 text-sm text-gray-400">
          Connecting to your health system...
        </p>
      </div>
    </div>
  );
}
